/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { ReviewCandidate } from '../interface/types';

/**
 * Formats YMME (Year, Make, Model, Engine) vehicle data into a display string.
 *
 * This function handles both PP (Product Portfolio) and ACES (Automotive Catalog Exchange Standard)
 * vehicle data formats. For ACES compatibility, subModel and engine are optional.
 *
 * @param ymme - Vehicle YMME object from ReviewCandidate or null
 * @returns Formatted vehicle string (e.g., "2020 Honda Civic Si 2.0L L4") or null if invalid
 *
 * @example
 * // PP format (no subModel)
 * formatYmmeVehicleString({ year: "2020", make: "Honda", model: "Civic", subModel: null, engine: "2.0L" })
 * // Returns: "2020 Honda Civic 2.0L"
 *
 * @example
 * // ACES format with subModel
 * formatYmmeVehicleString({ year: "2020", make: "Honda", model: "Civic", subModel: "Si", engine: "2.0L" })
 * // Returns: "2020 Honda Civic Si 2.0L"
 *
 * @example
 * // Missing optional fields
 * formatYmmeVehicleString({ year: "2020", make: "Honda", model: "Civic", subModel: null, engine: null })
 * // Returns: "2020 Honda Civic"
 */
export const formatYmmeVehicleString = (
  ymme: ReviewCandidate['ymme'] | undefined,
): string | null => {
  if (!ymme?.year || !ymme.make || !ymme.model) {
    return null;
  }

  const parts = [
    ymme.year,
    ymme.make,
    ymme.model,
    ymme.subModel, // Optional in ACES
    ymme.engine, // Optional in ACES
  ].filter(Boolean);

  return parts.join(' ');
};
